import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, useRouteError } from 'react-router';
import './index.css';
import { ApiError } from './lib/api.ts';
import { RequireAuth } from './lib/auth.tsx';
import { Layout } from './components/Layout.tsx';
import { ErrorState, Spinner } from './components/ui.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2,
    },
  },
});

// Cada página vira um chunk próprio, baixado na primeira visita à rota.
const page = <T,>(load: () => Promise<T>, pick: (m: T) => ComponentType) => async () => ({
  Component: pick(await load()),
});

// Depois de um deploy, uma aba antiga pede chunks que já não existem: recarregar busca o index.html novo.
function RouteError() {
  return (
    <div className="mx-auto max-w-xl p-6">
      <ErrorState error={useRouteError()} onRetry={() => window.location.reload()} />
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: '/login',
    hydrateFallbackElement: <Spinner />,
    errorElement: <RouteError />,
    lazy: page(() => import('./pages/Login.tsx'), (m) => m.LoginPage),
  },
  {
    hydrateFallbackElement: <Spinner />,
    errorElement: <RouteError />,
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: [
      { path: '/', lazy: page(() => import('./pages/Dashboard.tsx'), (m) => m.DashboardPage) },
      { path: '/board', lazy: page(() => import('./pages/Board.tsx'), (m) => m.BoardPage) },
      { path: '/backlog', lazy: page(() => import('./pages/Backlog.tsx'), (m) => m.BacklogPage) },
      { path: '/roadmap', lazy: page(() => import('./pages/Roadmap.tsx'), (m) => m.RoadmapPage) },
      { path: '/settings/notifications', lazy: page(() => import('./pages/NotificationSettings.tsx'), (m) => m.NotificationSettingsPage) },
      { path: '/sprints', lazy: page(() => import('./pages/Sprints.tsx'), (m) => m.SprintsPage) },
      { path: '/sprints/:id', lazy: page(() => import('./pages/SprintDetail.tsx'), (m) => m.SprintDetailPage) },
      { path: '/releases', lazy: page(() => import('./pages/Releases.tsx'), (m) => m.ReleasesPage) },
      { path: '/releases/:id', lazy: page(() => import('./pages/ReleaseDetail.tsx'), (m) => m.ReleaseDetailPage) },
      { path: '/reports/agents', lazy: page(() => import('./pages/AgentReport.tsx'), (m) => m.AgentReportPage) },
      { path: '/templates', lazy: page(() => import('./pages/Templates.tsx'), (m) => m.TemplatesPage) },
      { path: '/stories', lazy: page(() => import('./pages/Stories.tsx'), (m) => m.StoriesPage) },
      { path: '/tasks', lazy: page(() => import('./pages/TaskList.tsx'), (m) => m.TaskListPage) },
      { path: '/consultas', lazy: page(() => import('./pages/Search.tsx'), (m) => m.SearchPage) },
      { path: '/tasks/:key', lazy: page(() => import('./pages/TaskDetail.tsx'), (m) => m.TaskDetailPage) },
      { path: '/projects', lazy: page(() => import('./pages/Projects.tsx'), (m) => m.ProjectsPage) },
      { path: '/projects/new', lazy: page(() => import('./pages/ProjectForm.tsx'), (m) => m.ProjectFormPage) },
      { path: '/projects/:key', lazy: page(() => import('./pages/ProjectDetail.tsx'), (m) => m.ProjectDetailPage) },
      { path: '/projects/:key/docs/:docId?', lazy: page(() => import('./pages/Documents.tsx'), (m) => m.DocumentsPage) },
      { path: '/docs', lazy: page(() => import('./pages/Documents.tsx'), (m) => m.DocumentsIndexPage) },
      { path: '/projects/:key/edit', lazy: page(() => import('./pages/ProjectForm.tsx'), (m) => m.ProjectFormPage) },
      { path: '/reports/time', lazy: page(() => import('./pages/TimeReport.tsx'), (m) => m.TimeReportPage) },
      { path: '/labels', lazy: page(() => import('./pages/Labels.tsx'), (m) => m.LabelsPage) },
      { path: '/activity', lazy: page(() => import('./pages/Activity.tsx'), (m) => m.ActivityPage) },
      { path: '/admin/agents', lazy: page(() => import('./pages/Agents.tsx'), (m) => m.AgentsPage) },
      { path: '/admin/users', lazy: page(() => import('./pages/Users.tsx'), (m) => m.UsersPage) },
      { path: '/admin/board', lazy: page(() => import('./pages/BoardSettings.tsx'), (m) => m.BoardSettingsPage) },
      { path: '/account', lazy: page(() => import('./pages/Account.tsx'), (m) => m.AccountPage) },
      { path: '*', lazy: page(() => import('./pages/NotFound.tsx'), (m) => m.NotFoundPage) },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
